import { Channel } from "@redotech/cockroachdb-codegen/congodb/channel";
import { DraftReturnSource } from "@redotech/cockroachdb-codegen/congodb/draft-return-source";
import { DisplayReturn } from "@redotech/customer-portal-sdk/rpc/schema/get-returns-by-order-id";
import { useRequiredContext } from "@redotech/react-util/context";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { useTranslation } from "@redotech/redo-i18n";
import { Order } from "@redotech/redo-order-core-common/order";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import {
  CustomStatusSubtitles,
  getReturnHeaderText,
  getReturnStatusDescription,
} from "@redotech/redo-return-core-common/returns/return-display-utils";
import { LineItem } from "@redotech/redo-shopify-order-common/shopify-order";
import { getDateString } from "@redotech/util/date-format";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { SettingsContext } from "../../../contexts/settings";

// Define ReturnedLineItem interface
export interface ReturnedLineItem extends LineItem {
  _returnId?: string;
  _returnedItem?: boolean;
}

export const orderReturnsQueryKey = (orderId: string) => [
  "orderReturns",
  orderId,
];

export const returnableItemsQueryKey = (orderId: string) => [
  "returnableItems",
  orderId,
];

export function useOrderReturns(order: Order) {
  const { t, i18n } = useTranslation();
  const orderId = order._id;
  const rpcClient = useRequiredContext(RpcClientContext);
  const settings = useContext(SettingsContext);

  const customSubtitles: CustomStatusSubtitles = {
    returnStatusSubtitle:
      settings?.resourceOverride?.COMMON?.ORDER_DETAILS_RETURN_STATUS_SUBTITLE,
    exchangeStatusSubtitle:
      settings?.resourceOverride?.COMMON
        ?.ORDER_DETAILS_EXCHANGE_STATUS_SUBTITLE,
    storeCreditStatusSubtitle:
      settings?.resourceOverride?.COMMON
        ?.ORDER_DETAILS_STORE_CREDIT_STATUS_SUBTITLE,
  };

  const returnsQuery = useQuery({
    queryKey: orderReturnsQueryKey(orderId),
    queryFn: () => rpcClient.getReturnsByOrderId({ orderId }),
    enabled: !!rpcClient && !!orderId,
  });

  const returnsEnabled = settings?.returns?.enabled;

  const draftReturnQuery = useQuery({
    queryKey: returnableItemsQueryKey(orderId),
    queryFn: () =>
      rpcClient.createDraftReturnFromSource({
        source: DraftReturnSource.FromOrder,
        orderId,
        returnType: ReturnTypeEnum.RETURN,
        channel: Channel.Web,
      }),
    enabled: !!rpcClient && !!orderId && !!returnsEnabled,
  });

  const returns = returnsQuery.data?.returns ?? [];

  return {
    returnsQuery,
    draftReturnQuery,

    getReturnsByReturnId: () => groupReturnsByReturnId(returns),

    getReturnCompletionMessage: (returnId: string) => {
      const returnData = returns.find((r) => r._id === returnId);
      if (!returnData) {
        return t("This was rejected.");
      }
      return getReturnStatusDescription({
        status: returnData.status,
        createdAt: returnData.createdAt,
        dateFormatter: (date) => getDateString(date, { locale: i18n.language }),
        returnType: returnData.returnTypes,
        type: returnData.type,
        customSubtitles,
      });
    },

    getReturnHeaderText: (returnId: string) => {
      const returnData = returns.find((r) => r._id === returnId);
      if (!returnData) {
        return t("This was rejected");
      }
      return getReturnHeaderText({
        status: returnData.status,
        returnType: returnData.returnTypes,
        type: returnData.type,
      });
    },
  };
}

function groupReturnsByReturnId(
  returns: DisplayReturn[],
): Map<string, DisplayReturn> {
  return new Map(returns.map((r) => [r._id, r]));
}
